import { ApplicationRef, computed, inject, Injectable, NgZone, signal } from '@angular/core';

import { toAppError } from '../../../shared/utils/app-error.util';
import { translateNow, translationVersion } from '../i18n/translate-now';
import { ToastNotificationService } from './toast-notification.service';

export interface ReviewChangesRequest {
  /** Literal heading; wins over {@link titleKey}. Prefer the key so the dialog follows language changes. */
  title?: string;
  /** Catalog key of the heading; defaults to `common.viewer.reviewChanges`. */
  titleKey?: string;
  /** Values for the `{name}` placeholders of {@link titleKey}. */
  titleParams?: Record<string, unknown>;
  /** Literal subtitle; wins over {@link subtitleKey}. */
  subtitle?: string;
  /** Catalog key of the subtitle. */
  subtitleKey?: string;
  /** Values for the `{name}` placeholders of {@link subtitleKey}. */
  subtitleParams?: Record<string, unknown>;
  originalContent: string;
  modifiedContent: string;
  onConfirm: () => Promise<void> | void;
  onDiscard?: () => void;
}

/** Dialog text kept as a catalog key (or literal) so it is rebuilt when the language changes. */
interface TextSource {
  key?: string;
  params?: Record<string, unknown>;
  literal?: string;
}

function resolve(source: TextSource): string {
  translationVersion();
  return source.key ? translateNow(source.key, source.params) : (source.literal ?? '');
}

@Injectable({ providedIn: 'root' })
export class ReviewChangesService {
  private readonly appRef  = inject(ApplicationRef);
  private readonly ngZone  = inject(NgZone);
  private readonly toast   = inject(ToastNotificationService);

  readonly isOpen = signal(false);
  readonly isSubmitting = signal(false);
  private readonly titleSource = signal<TextSource>({ key: 'common.viewer.reviewChanges' });
  private readonly subtitleSource = signal<TextSource>({ literal: '' });
  /** Heading shown now, in the active language. */
  readonly title = computed(() => resolve(this.titleSource()));
  /** Subtitle shown now, in the active language. */
  readonly subtitle = computed(() => resolve(this.subtitleSource()));
  readonly originalContent = signal('');
  readonly modifiedContent = signal('');

  private pendingConfirm: (() => Promise<void> | void) | null = null;
  private pendingDiscard: (() => void) | null = null;

  open(request: ReviewChangesRequest): void {
    // Run inside NgZone so that OnPush components detect the signal change
    // even when the caller ran outside Angular's zone (e.g. from tb-form).
    this.ngZone.run(() => {
      this.titleSource.set(
        request.title !== undefined
          ? { literal: request.title }
          : { key: request.titleKey ?? 'common.viewer.reviewChanges', params: request.titleParams },
      );
      this.subtitleSource.set(
        request.subtitle !== undefined
          ? { literal: request.subtitle }
          : request.subtitleKey
            ? { key: request.subtitleKey, params: request.subtitleParams }
            : { literal: '' },
      );
      this.originalContent.set(request.originalContent);
      this.modifiedContent.set(request.modifiedContent);
      this.pendingConfirm = request.onConfirm;
      this.pendingDiscard = request.onDiscard ?? null;
      this.isSubmitting.set(false);
      this.isOpen.set(true);
    });
  }

  async confirm(): Promise<void> {
    if (!this.pendingConfirm) return;
    this.isSubmitting.set(true);
    try {
      await this.pendingConfirm();
      this.close();
    } catch (err) {
      // The review dialog has no inline error area, so the failure goes through the single toast
      // entry point. The dialog stays open (no close()) so the user can see what they submitted and retry.
      this.toast.error(toAppError(err));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  discard(): void {
    this.pendingDiscard?.();
    this.close();
  }

  private close(): void {
    this.ngZone.run(() => {
      this.isOpen.set(false);
      this.pendingConfirm = null;
      this.pendingDiscard = null;
    });
  }
}
